import React from "react";

import {
  getSelectedLayerKeyboardAction,
  isEditableKeyboardTarget,
} from "./selectedLayerKeyboardShortcuts";

type TSelectedLayerId = number | string;

const useSelectedLayerKeyboardShortcuts = <T extends TSelectedLayerId>({
  selectedLayerId,
  enabled = true,
  onDelete,
  onDuplicate,
}: {
  selectedLayerId: T | null;
  enabled?: boolean;
  onDelete: (layerId: T) => void;
  onDuplicate?: (layerId: T) => void;
}) => {
  React.useEffect(() => {
    if (!enabled || selectedLayerId === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const action = getSelectedLayerKeyboardAction(event);
      if (!action) return;
      if (action === "duplicate" && !onDuplicate) return;
      if (isEditableKeyboardTarget(document.activeElement)) return;

      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      event.preventDefault();
      event.stopPropagation();

      if (action === "duplicate") {
        onDuplicate?.(selectedLayerId);
        return;
      }

      onDelete(selectedLayerId);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, onDelete, onDuplicate, selectedLayerId]);
};

export default useSelectedLayerKeyboardShortcuts;
